import { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "src/common/state/hooks";
import { useNavigate } from "react-router-dom";
import { Input } from "../../../common/components/Input";
import type { IForgottenPasswordFormData } from "../state/authSlice";
import { forgottenPassword } from "../state/authSlice";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import { Error } from "../../../common/components/AppState/Error";
import { LoginPageLayout } from "./LoginForm";
import Icon from "src/common/components/Icon/Icon";

export const FORGOTTEN_PASSWORD_COMPONENT_ID = "forgottenPasswordComponent";

interface IForgottenPasswordForm {
  onSubmit: (data: IForgottenPasswordFormData) => void;
}

const ForgottenPasswordContainer = () => {
  const dispatch = useAppDispatch();
  const [hasEmailBeenSent, setHasEmailBeenSent] = useState(false);

  const onSubmit = async (data: IForgottenPasswordFormData) => {
    await dispatch(
      forgottenPassword({ componentId: FORGOTTEN_PASSWORD_COMPONENT_ID, ...data }),
    ).finally(() => {
      setHasEmailBeenSent(true);
    });
  };

  if (hasEmailBeenSent) {
    return <ForgottenPasswordFeedback />;
  } else {
    return <ForgottenPassword onSubmit={onSubmit} />;
  }
};

const ForgottenPassword = ({ onSubmit }: IForgottenPasswordForm) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register, handleSubmit } = useForm<IForgottenPasswordFormData>();
  const inputLabel = t("pages.login.forgotPassword.fillEmail");

  return (
    <LoginPageLayout>
      <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
        <div className="my-2">
          <h2 className="text-xl font-medium text-black">
            {t("pages.login.forgotPassword.title")}
          </h2>
        </div>
        <Error componentId={FORGOTTEN_PASSWORD_COMPONENT_ID} />
        <IsLoading componentId={FORGOTTEN_PASSWORD_COMPONENT_ID} showSuccess={false} />
        <Input
          autoFocus
          type="email"
          label={inputLabel}
          placeholder={t("pages.login.emailPlaceholder")}
          registerFct={() => register("email", { required: true, pattern: /^\S+@\S+$/i })}
        />
        <div className="flex flex-col sm:flex-row sm:pt-8 sm:justify-between">
          <button
            onClick={() => navigate("/")}
            className="btn-alternative-outline mb-2 sm:mb-0"
            type="button"
          >
            {t("general.back")}
          </button>
          <button className="btn-primary-fill" type="submit">
            {t("pages.login.forgotPassword.sendMeALink")}
          </button>
        </div>
      </form>
    </LoginPageLayout>
  );
};

const ForgottenPasswordFeedback = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.focus();
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      navigate("/");
    }
  };

  return (
    <LoginPageLayout>
      <div
        className="flex flex-col items-center outline-none"
        onKeyDown={handleKeyDown}
        tabIndex={0}
        ref={containerRef}
      >
        <Icon icon="CheckIcon" className="text-green-500 w-20 h-20" />
        <h2 className="text-xl font-medium text-black text-center">
          {t("pages.login.forgotPassword.mailSent")}
        </h2>
        <button className="btn-primary-fill mb-2 mt-6 sm:mb-0" onClick={() => navigate("/")}>
          {t("general.ok")}
        </button>
      </div>
    </LoginPageLayout>
  );
};

export default ForgottenPasswordContainer;
